import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';
import type { ListDaysWithSummariesOutput } from '../../lib/api/content-api-client';

/**
 * Days slice state (task 5.2, public-website spec).
 *
 * Per-page keyed cache of `ListDaysWithSummariesV1` payloads per
 * design §3.1 (Slices layer): one entry per requested page in
 * `byPage`, plus a parallel `statusByPage` and `errorByPage` so the
 * `/days` page can render per-page loading and error states. Page
 * size is server-pinned to 20 per Property 17, so the page index is
 * sufficient on its own as the cache key.
 *
 * Reducers are pure — Redux Toolkit's `createSlice` wraps every
 * mutation in Immer so the assignments below are safe inside the
 * `produce` proxy and produce a new immutable state object.
 *
 * Function-length budget: every reducer below stays under 20 lines
 * per Requirement 3.7.
 */

/** Per-page fetch status discriminator. */
export type DaysFetchStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

/** Days slice state shape. */
export interface DaysState {
  /** Per-page cache of `ListDaysWithSummariesV1` payloads. */
  readonly byPage: Record<number, ListDaysWithSummariesOutput>;
  /** Per-page fetch status. Absent key = `idle`. */
  readonly statusByPage: Record<number, DaysFetchStatus>;
  /** Per-page last error message (cleared on subsequent request). */
  readonly errorByPage: Record<number, string | undefined>;
}

const initialState: DaysState = {
  byPage: {},
  statusByPage: {},
  errorByPage: {},
};

export const daysSlice = createSlice({
  name: 'days',
  initialState,
  reducers: {
    /** Mark a page as in-flight; clear any prior error. */
    daysFetchRequested(state, action: PayloadAction<{ page: number }>) {
      state.statusByPage[action.payload.page] = 'loading';
      state.errorByPage[action.payload.page] = undefined;
    },
    /** Cache the payload for a page and mark the fetch succeeded. */
    daysFetchSucceeded(
      state,
      action: PayloadAction<{ page: number; payload: ListDaysWithSummariesOutput }>,
    ) {
      state.byPage[action.payload.page] = castDraft(action.payload.payload);
      state.statusByPage[action.payload.page] = 'succeeded';
      state.errorByPage[action.payload.page] = undefined;
    },
    /** Record the failure message for a page. */
    daysFetchFailed(
      state,
      action: PayloadAction<{ page: number; error: string }>,
    ) {
      state.statusByPage[action.payload.page] = 'failed';
      state.errorByPage[action.payload.page] = action.payload.error;
    },
  },
});

export const {
  daysFetchRequested,
  daysFetchSucceeded,
  daysFetchFailed,
} = daysSlice.actions;

export default daysSlice.reducer;
